// src/composables/usePagination.ts
import { ref, Ref } from "vue";
import * as common from "@/assets/common/common";
import { QueryInfo } from "@/model/base";

type FetchApiCallable<T, F = Record<string, any>> = (
  params: {
    pageSize: number;
    lastEvaluatedKey: string | null;
  } & F
) => Promise<any>;

export function usePagination<T, F extends Record<string, any> = {}>(
  fetchApi: FetchApiCallable<T, F>,
  initialPageSize: number = 10,
  initialFilterParams?: F
) {
  const paginatedItems = ref<T[]>([]) as Ref<T[]>;
  const listLoading = ref(false);
  const error = ref<string | null>(null);
  const currentPage = ref(1);
  const hasNextPage = ref(true);

  const pagination = ref<QueryInfo>({
    pageSize: initialPageSize,
    lastEvaluatedKey: null,
    previousLastEvaluatedKeys: [],
  });

  const currentFilterParams = ref(initialFilterParams) as Ref<F | undefined>;

  async function fetchPaginatedData(direction: "next" | "prev" | "current" = "current", filters?: F) {
    listLoading.value = true;
    error.value = null;

    let queryLastEvaluatedKey: string | null = null;

    if (filters) {
      currentFilterParams.value = filters;
    }

    if (direction === "current") {
      currentPage.value = 1;
      pagination.value.lastEvaluatedKey = null;
      pagination.value.previousLastEvaluatedKeys = [];
      queryLastEvaluatedKey = null;
    } else if (direction === "next") {
      queryLastEvaluatedKey = pagination.value.lastEvaluatedKey ?? null;
    } else {
      // "prev"
      if (currentPage.value === 1) {
        queryLastEvaluatedKey = null;
      } else {
        if (pagination.value.previousLastEvaluatedKeys) {
          queryLastEvaluatedKey = pagination.value.previousLastEvaluatedKeys[currentPage.value - 2] || null;
        } else {
          queryLastEvaluatedKey = null;
        }
      }
    }

    try {
      const apiParams = {
        pageSize: pagination.value.pageSize,
        lastEvaluatedKey: queryLastEvaluatedKey,
        ...(currentFilterParams.value || {}),
      } as { pageSize: number; lastEvaluatedKey: string | null } & F;
      const result = await fetchApi(apiParams);

      paginatedItems.value = common.getItemListFromFunctionResult(result) as T[];
      const newLastEvaluatedKey = common.getLastEvaluatedKey(result);
      hasNextPage.value = !!newLastEvaluatedKey;

      if (direction === "next") {
        if (queryLastEvaluatedKey !== null && pagination.value.previousLastEvaluatedKeys) {
          if (!pagination.value.previousLastEvaluatedKeys.includes(queryLastEvaluatedKey)) {
            pagination.value.previousLastEvaluatedKeys.push(queryLastEvaluatedKey);
          }
        }
      }
      pagination.value.lastEvaluatedKey = newLastEvaluatedKey;
    } catch (err: any) {
      console.error("페이지네이션 데이터 로딩 실패:", err);
      error.value = "데이터를 불러오는데 실패했습니다.";
      paginatedItems.value = [];
      hasNextPage.value = false;
    } finally {
      listLoading.value = false;
    }
  }

  function goToNextPage() {
    if (hasNextPage.value) {
      currentPage.value++;
      fetchPaginatedData("next");
    }
  }

  function goToPreviousPage() {
    if (currentPage.value > 1) {
      currentPage.value--;
      fetchPaginatedData("prev");
    }
  }

  function refreshData(filters?: F) {
    fetchPaginatedData("current", filters);
  }

  return {
    paginatedItems,
    listLoading,
    error,
    currentPage,
    hasNextPage,
    goToNextPage,
    goToPreviousPage,
    refreshData,
    // pagination, // 디버깅이나 특별한 경우를 위해 pagination 상태 전체를 노출할 수 있음
  };
}
